import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { useAdminSessionSummary } from "../../auth/hooks/useAdminSessionSummary";
import type { DashboardAsset, DashboardProject } from "../types.js";
import {
  fetchDashboardAssetInventory,
  getDashboardAssetInventoryViewModel,
} from "../assets/api/assetsApi";
import {
  getDashboardProjectsListData,
  fetchDashboardProjectsList,
  validateDashboardProjectsListDto,
} from "../projects/api/projectsApi";
import {
  mapProjectRecordsToViewModels,
  getProjectsDisplayStatus,
} from "../projects/mappers/projectsMappers";
import { mapAssetInventoryToViewModel } from "../assets/mappers/assetsMappers";
import { getDashboardAssetTypeLabel } from "../assets/api/assetsApi";
import {
  getCachedDashboardAssetInventory,
  getCachedDashboardProjects,
  setCachedDashboardAssetInventory,
  setCachedDashboardProjects,
} from "./dashboardPreviousDataCache";

export interface DashboardGlobalSearchResultProject {
  kind: "project";
  id: string;
  title: string;
  subtitle: string;
  sectionId: "projects";
  project: DashboardProject;
}

export interface DashboardGlobalSearchResultAsset {
  kind: "asset";
  id: string;
  title: string;
  subtitle: string;
  sectionId: "assets";
  asset: DashboardAsset;
}

export type DashboardGlobalSearchResult =
  | DashboardGlobalSearchResultProject
  | DashboardGlobalSearchResultAsset;

export interface DashboardGlobalSearchGroup {
  label: string;
  items: DashboardGlobalSearchResult[];
}

export interface UseDashboardGlobalSearchResult {
  assets: DashboardAsset[];
  errorMessage: string | null;
  groups: DashboardGlobalSearchGroup[];
  isLoading: boolean;
  projects: DashboardProject[];
  query: string;
  setQuery: (value: string) => void;
}

function normalizeSearchText(value: string): string {
  return value.toLowerCase().trim();
}

function buildProjectSearchText(project: DashboardProject): string {
  return normalizeSearchText(
    [
      project.name,
      project.companyName,
      project.updatedAtLabel,
      project.status,
      ...project.collaborators,
      getProjectsDisplayStatus(project.status),
    ].join(" "),
  );
}

function buildAssetSearchText(asset: DashboardAsset): string {
  return normalizeSearchText(
    [
      asset.name,
      asset.type,
      asset.readiness,
      asset.sizeLabel,
      asset.updatedAtLabel,
      asset.description,
      ...asset.metadata.flatMap((entry) => [entry.label, entry.value]),
      getDashboardAssetTypeLabel(asset.type),
    ].join(" "),
  );
}

function mapProjectRecordToSearchResult(
  project: DashboardProject,
): DashboardGlobalSearchResultProject {
  return {
    kind: "project",
    id: project.id,
    title: project.name,
    subtitle: `${project.companyName} - ${getProjectsDisplayStatus(project.status)}`,
    sectionId: "projects",
    project,
  };
}

function mapAssetToSearchResult(asset: DashboardAsset): DashboardGlobalSearchResultAsset {
  return {
    kind: "asset",
    id: asset.id,
    title: asset.name,
    subtitle: `${getDashboardAssetTypeLabel(asset.type)} - ${asset.readiness} - ${asset.sizeLabel}`,
    sectionId: "assets",
    asset,
  };
}

function filterDashboardSearchResults(
  projects: ReadonlyArray<DashboardProject>,
  assets: ReadonlyArray<DashboardAsset>,
  query: string,
): DashboardGlobalSearchGroup[] {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) {
    return [];
  }

  const projectItems = projects
    .filter((project) => buildProjectSearchText(project).includes(normalizedQuery))
    .map(mapProjectRecordToSearchResult);
  const assetItems = assets
    .filter((asset) => buildAssetSearchText(asset).includes(normalizedQuery))
    .map(mapAssetToSearchResult);

  return [
    { label: "Projects", items: projectItems },
    { label: "Assets", items: assetItems },
  ].filter((group) => group.items.length > 0);
}

export function useDashboardGlobalSearch(): UseDashboardGlobalSearchResult {
  const { sessionUser } = useAdminSessionSummary();
  const principalKey =
    sessionUser?.id?.trim() || sessionUser?.email?.trim() || "anonymous";
  const [projects, setProjects] = useState<DashboardProject[]>(() => {
    const cachedProjects = getCachedDashboardProjects(principalKey);
    if (cachedProjects) {
      return cachedProjects;
    }

    return mapProjectRecordsToViewModels(getDashboardProjectsListData().items);
  });
  const [assets, setAssets] = useState<DashboardAsset[]>(() => {
    const cachedInventory = getCachedDashboardAssetInventory(principalKey);
    if (cachedInventory) {
      return cachedInventory.assets;
    }

    return getDashboardAssetInventoryViewModel().assets;
  });
  const [query, setQuery] = useState("");
  const [isLoading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const deferredQuery = useDeferredValue(query);

  useEffect(() => {
    let disposed = false;

    async function hydrateSearchIndex() {
      setLoading(true);
      setErrorMessage(null);

      try {
        const [projectsResult, assetsResult] = await Promise.all([
          fetchDashboardProjectsList(),
          fetchDashboardAssetInventory(),
        ]);

        if (disposed) {
          return;
        }

        const nextProjects = projectsResult.ok
          ? validateDashboardProjectsListDto(projectsResult.data)
            ? mapProjectRecordsToViewModels(projectsResult.data.items)
            : []
          : [];

        const nextAssets = assetsResult.ok
          ? mapAssetInventoryToViewModel(assetsResult.data).assets
          : [];
        const nextAssetInventory = assetsResult.ok
          ? mapAssetInventoryToViewModel(assetsResult.data)
          : null;

        let nextError: string | null = null;
        if (!projectsResult.ok) {
          nextError = projectsResult.error.message;
        } else if (!validateDashboardProjectsListDto(projectsResult.data)) {
          nextError = "Invalid projects list payload.";
        }

        if (!assetsResult.ok) {
          nextError = nextError ?? assetsResult.error.message;
        }

        setProjects((currentProjects) => {
          if (nextProjects.length === 0 && currentProjects.length > 0) {
            return currentProjects;
          }

          setCachedDashboardProjects(principalKey, nextProjects);
          return nextProjects;
        });
        setAssets((currentAssets) => {
          if (nextAssets.length === 0 && currentAssets.length > 0) {
            return currentAssets;
          }

          if (nextAssetInventory) {
            setCachedDashboardAssetInventory(principalKey, nextAssetInventory);
          }
          return nextAssets;
        });
        setErrorMessage(nextError);
      } catch (error) {
        if (!disposed) {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Unable to load search results right now.",
          );
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    }

    void hydrateSearchIndex();

    return () => {
      disposed = true;
    };
  }, [principalKey]);

  const groups = useMemo(
    () => filterDashboardSearchResults(projects, assets, deferredQuery),
    [assets, deferredQuery, projects],
  );

  return {
    assets,
    errorMessage,
    groups,
    isLoading,
    projects,
    query,
    setQuery,
  };
}
